import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowUpRight, ArrowRight, ShieldCheck, Clock, Award, Users, Truck, BadgeCheck, TrendingUp, SearchCheck, ClipboardList, Rocket, LineChart, Play, Factory, HardHat, Stethoscope, Building2, Landmark, Warehouse } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/Reveal";
import { TeamBand } from "@/components/TeamBand";
import { VideoPresentation } from "@/components/VideoPresentation";
import { LeadMagnet } from "@/components/LeadMagnet";
import { Stars } from "@/components/Stars";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";

const CHAPTERS = [
  { n: "01", t: "Conseil stratégique QSE", te: "Strategic HSE consulting", d: "Audits, document unique, feuilles de route conformité ISO 45001 : nous structurons votre politique santé-sécurité comme un véritable levier de gouvernance et de compétitivité.", de: "Audits, risk assessments, ISO 45001 compliance roadmaps: we structure your health & safety policy as a true lever of governance and competitiveness." },
  { n: "02", t: "Ergonomie & performance", te: "Ergonomics & performance", d: "Chaque poste de travail est un gisement de productivité. Nos solutions ergonomiques réduisent les TMS, l'absentéisme et libèrent le potentiel de vos équipes.", de: "Every workstation is a productivity reserve. Our ergonomic solutions cut MSDs and absenteeism, unlocking your teams' potential." },
  { n: "03", t: "Équipements de protection", te: "Protective equipment", d: "Une gamme d'EPI certifiés CE, sélectionnée avec l'exigence d'un cabinet : nous ne recommandons que ce que nous auditerions.", de: "A range of CE-certified PPE, selected with a consultancy's rigour: we only recommend what we would audit ourselves." },
  { n: "04", t: "Académie de formation", te: "Training academy", d: "193 programmes certifiants — SST, PRAP, management de la prévention — pour transformer chaque collaborateur en acteur de la culture sécurité.", de: "193 certified programmes — first aid, manual handling, prevention leadership — turning every employee into a safety-culture ambassador." },
];

const CONVICTIONS = [
  { n: "01", ft: "La prévention est un investissement, jamais une dépense.", et: "Prevention is an investment, never an expense.", fd: "Chaque euro engagé dans la santé-sécurité en rapporte plus de deux. Les organisations les plus performantes l'ont compris : elles ne subissent pas la réglementation, elles en font un levier stratégique.", ed: "Every euro committed to health & safety returns more than two. Top-performing organisations understand this: they don't endure regulation, they turn it into a strategic lever." },
  { n: "02", ft: "Pas de performance durable sans capital humain protégé.", et: "No lasting performance without protected human capital.", fd: "Vos équipes sont votre premier actif. Un collaborateur en sécurité est plus engagé, plus fidèle, plus productif. La marque employeur se construit d'abord sur le terrain.", ed: "Your people are your primary asset. A safe employee is more engaged, more loyal, more productive. Employer branding is built on the ground first." },
  { n: "03", ft: "La conformité est un point de départ, pas une ligne d'arrivée.", et: "Compliance is a starting point, not a finish line.", fd: "Être conforme protège des sanctions. Viser l'excellence protège des accidents. Nous accompagnons nos clients au-delà des obligations, vers une culture de prévention qui fait la différence.", ed: "Compliance protects against sanctions. Striving for excellence protects against accidents. We take our clients beyond obligations, towards a prevention culture that sets them apart." },
];

export default function Home() {
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [featured, setFeatured] = useState(null);
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef(null);
  const { t, lang } = useLang();
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 120]);

  useEffect(() => {
    api.get("/products").then((r) => {
      const nouveaux = r.data.filter((p) => p.badge === "Nouveau");
      setProducts([...nouveaux, ...r.data.filter((p) => p.badge !== "Nouveau")].slice(0, 3));
    }).catch(() => {});
    api.get("/formations").then((r) => {
      const best = [...r.data].sort((a, b) => (b.rating_avg * b.rating_count) - (a.rating_avg * a.rating_count))[0];
      setFeatured(best || null);
    }).catch(() => {});
    Promise.all([api.get("/reviews/featured"), api.get("/formation-reviews/featured")]).then(([p, f]) => {
      const prods = p.data.map((r) => ({ ...r, kind: "product" }));
      const forms = f.data.map((r) => ({ ...r, kind: "formation" }));
      const mix = [];
      for (let i = 0; i < 6 && mix.length < 6; i++) {
        if (prods[i]) mix.push(prods[i]);
        if (forms[i] && mix.length < 6) mix.push(forms[i]);
      }
      setReviews(mix);
    }).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-screen pt-[148px] flex items-center overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 w-full grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7 pt-16 lg:pt-0">
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
              className="overline mb-6" style={{ color: "var(--orange)" }}>
              {t("Cabinet de conseil QSE · Équipements · Formation — Europe & Afrique", "HSE consulting firm · Equipment · Training — Europe & Africa")}
            </motion.p>
            <h1 className="font-display font-black leading-[0.92] tracking-tight text-5xl sm:text-6xl lg:text-[5.2rem]">
              <MaskedLines key={lang} lines={lang === "fr"
                ? ["La sécurité n'est pas", <>un coût. C'est votre</>, <><span style={{ color: "var(--orange)" }}>avantage compétitif</span>.</>]
                : ["Safety is not", <>a cost. It is your</>, <><span style={{ color: "var(--orange)" }}>competitive edge</span>.</>]} />
            </h1>
            <Reveal delay={0.6}>
              <p className="mt-8 text-lg md:text-xl max-w-xl" style={{ color: "var(--ink-2)" }}>
                {t("Nous aidons dirigeants et responsables HSE à transformer la prévention en performance mesurable : conseil stratégique QSE, équipements ergonomiques, EPI certifiés et 193 formations. En Europe et en Afrique francophone.",
                   "We help executives and HSE leaders turn prevention into measurable performance: strategic HSE consulting, ergonomic equipment, certified PPE and 193 training courses. Across Europe and French-speaking Africa.")}
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Link to="/contact" data-testid="hero-devis-btn" className="btn-primary px-8 py-4 font-semibold flex items-center justify-center gap-2">
                  {t("Parler à un expert", "Talk to an expert")} <ArrowRight size={18} />
                </Link>
                <Link to="/catalogue" data-testid="hero-catalogue-btn" className="btn-outline px-8 py-4 font-semibold flex items-center justify-center gap-2">
                  {t("Explorer nos solutions", "Explore our solutions")}
                </Link>
              </div>
              <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3" data-testid="hero-trust-strip">
                {[
                  { v: "5", fl: "pays d'intervention", el: "countries" },
                  { v: "2", fl: "continents", el: "continents" },
                  { v: "193", fl: "programmes de formation", el: "training programmes" },
                  { v: "4,6/5", fl: "satisfaction clients", el: "client satisfaction" },
                ].map((s) => (
                  <div key={s.fl} className="flex items-baseline gap-2">
                    <span className="font-display font-black text-2xl" style={{ color: "var(--ink)" }}>{s.v}</span>
                    <span className="text-xs uppercase tracking-wider" style={{ color: "var(--ink-2)" }}>{t(s.fl, s.el)}</span>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-5 relative">
            <motion.div style={{ y: heroY }} className="relative">
              <div className="overflow-hidden" style={{ border: "1px solid var(--border)" }}>
                <motion.img
                  initial={{ scale: 1.15, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                  src="https://static.prod-images.emergentagent.com/jobs/3605812b-40ae-4438-9f86-28dfb90c74fb/images/692c0ce5edc9b5ed6b4e2f754294b2f7f3e8240845303068dcfb39ad8ff3cdec.jpeg"
                  alt="Équipe diversifiée au travail" className="w-full h-[420px] lg:h-[560px] object-cover" />
              </div>
              <div className="absolute -bottom-6 -left-6 hidden md:block px-6 py-5" style={{ background: "var(--orange)", color: "var(--bone)" }}>
                <p className="font-display font-black text-3xl">+2 500</p>
                <p className="overline">{t("Postes équipés", "Workstations equipped")}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* MARQUEE */}
      <section style={{ borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }} className="py-6">
        <Marquee speed={40} gradient={false}>
          {["EXCELLENCE OPÉRATIONNELLE", "CONSEIL STRATÉGIQUE QSE", "CONFORMITÉ ISO 45001", "CAPITAL HUMAIN PROTÉGÉ", "PERFORMANCE DURABLE"].map((t, i) => (
            <span key={i} className="font-display font-extrabold text-2xl md:text-4xl mx-16 flex items-center gap-16" style={{ color: "var(--ink)" }}>
              {t} <span style={{ color: "var(--orange)" }}>✦</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* NUMBERED MANIFESTO */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-40">
        <Reveal><p className="overline mb-4" style={{ color: "var(--orange)" }}>{t("Nos pôles d'expertise", "Our practice areas")}</p></Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight max-w-4xl leading-none">
            {t("Quatre expertises intégrées, une ambition : faire de la prévention votre levier de performance.", "Four integrated practices, one ambition: making prevention your performance lever.")}
          </h2>
        </Reveal>
        <div className="mt-20 grid grid-cols-1 md:grid-cols-2">
          {CHAPTERS.map((c, i) => (
            <Reveal key={c.n} delay={i * 0.08}>
              <div className="py-10 md:p-12" style={{ borderTop: "1px solid var(--border)", borderLeft: i % 2 === 1 ? "1px solid var(--border)" : "none" }}>
                <span className="font-mono2 text-sm" style={{ color: "var(--orange)" }}>{c.n}</span>
                <h3 className="font-display font-bold text-3xl mt-4 mb-4">{t(c.t, c.te)}</h3>
                <p className="text-lg" style={{ color: "var(--ink-2)" }}>{t(c.d, c.de)}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* NOS CONVICTIONS */}
      <section style={{ background: "var(--ink)", color: "var(--bone)" }} className="py-24 md:py-40" data-testid="convictions-section">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal><p className="overline mb-4" style={{ color: "var(--orange)" }}>{t("Nos convictions", "Our beliefs")}</p></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none max-w-3xl">
              {t("Ce que nous croyons. Ce que nous prouvons.", "What we believe. What we prove.")}
            </h2>
          </Reveal>
          <div className="mt-20 grid grid-cols-1 lg:grid-cols-3 gap-px" style={{ background: "rgba(245,245,240,0.15)", border: "1px solid rgba(245,245,240,0.15)" }}>
            {CONVICTIONS.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.1}>
                <div className="p-8 md:p-10 h-full flex flex-col" style={{ background: "var(--ink)" }} data-testid={`conviction-${i}`}>
                  <span className="font-display font-black text-6xl md:text-7xl leading-none" style={{ color: "var(--orange)", opacity: 0.35 }}>{c.n}</span>
                  <h3 className="font-display font-bold text-2xl mt-6 mb-4 leading-tight">{t(c.ft, c.et)}</h3>
                  <p style={{ color: "rgba(245,245,240,0.7)" }}>{t(c.fd, c.ed)}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.15}>
            <div className="mt-14 md:mt-20 grid grid-cols-1 lg:grid-cols-12 gap-8 items-start pt-12" style={{ borderTop: "1px solid rgba(245,245,240,0.15)" }} data-testid="insight-quote">
              <p className="lg:col-span-2 overline" style={{ color: "var(--orange)" }}>{t("Point de vue", "Insight")}</p>
              <blockquote className="lg:col-span-10 font-display font-bold text-2xl md:text-4xl leading-snug tracking-tight">
                {t("« D'ici 2030, les organisations qui auront fait de la santé-sécurité un pilier stratégique attireront les meilleurs talents, remporteront les appels d'offres les plus exigeants et distanceront durablement leurs concurrents. Les autres suivront. »",
                   "“By 2030, organisations that make health & safety a strategic pillar will attract the best talent, win the most demanding tenders and durably outpace their competitors. The others will follow.”")}
                <footer className="mt-6 text-base font-normal font-body" style={{ color: "rgba(245,245,240,0.6)" }}>
                  — {t("Direction du conseil, ALDOL-SOLUTIONS", "Consulting leadership, ALDOL-SOLUTIONS")}
                </footer>
              </blockquote>
            </div>
          </Reveal>
        </div>
      </section>

      {/* POURQUOI ALDOL-SOLUTIONS */}
      <section style={{ background: "var(--white)", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }} className="py-24 md:py-40" data-testid="why-aldol-section">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <Reveal><p className="overline mb-4" style={{ color: "var(--orange)" }}>{t("Pourquoi ALDOL-SOLUTIONS", "Why ALDOL-SOLUTIONS")}</p></Reveal>
              <Reveal delay={0.05}>
                <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none">
                  {t("L'exigence d'un grand cabinet. La proximité d'un partenaire.", "The rigour of a top firm. The closeness of a partner.")}
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <p className="mt-8 text-lg" style={{ color: "var(--ink-2)" }}>
                  {t("Depuis Saint-Maur-des-Fossés, nous conseillons des organisations en France et en Afrique francophone en réunissant sous un même toit le conseil stratégique, les équipements et la formation. Un interlocuteur unique, une vision à 360° de votre sécurité : vous gagnez en cohérence, en vitesse d'exécution et en impact.",
                     "From Saint-Maur-des-Fossés, we advise organisations across France and French-speaking Africa, bringing strategic consulting, equipment and training under one roof. One single point of contact, a 360° view of your safety: you gain consistency, execution speed and impact.")}
                </p>
                <Link to="/services" data-testid="why-services-btn" className="btn-primary mt-10 px-8 py-4 font-semibold inline-flex items-center gap-2">
                  {t("Découvrir notre accompagnement", "Discover our services")} <ArrowRight size={18} />
                </Link>
              </Reveal>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px" style={{ background: "var(--border)", border: "1px solid var(--border)" }}>
              {[
                { icon: ShieldCheck, ft: "Expertise certifiée", et: "Certified expertise", fd: "Consultants QSE expérimentés, formateurs agréés et matériel conforme CE : chaque recommandation s'appuie sur les référentiels en vigueur (ISO 45001, INRS, Code du travail).", ed: "Experienced HSE consultants, accredited trainers and CE-compliant equipment: every recommendation follows current standards (ISO 45001, INRS, French labour code)." },
                { icon: Users, ft: "Interlocuteur unique", et: "Single point of contact", fd: "Matériel, audit, formation : un conseiller dédié pilote l'ensemble de votre projet, de l'analyse du besoin jusqu'au suivi après déploiement.", ed: "Equipment, audit, training: one dedicated advisor manages your entire project, from needs analysis to post-deployment follow-up." },
                { icon: Clock, ft: "Réactivité garantie", et: "Guaranteed responsiveness", fd: "Devis personnalisé sous 48h ouvrées, livraison express sous 48h pour les produits en stock et rappel gratuit sous 24h.", ed: "Personalised quote within 48 business hours, express 48h delivery on in-stock products and free callback within 24 hours." },
                { icon: TrendingUp, ft: "Résultats mesurables", et: "Measurable results", fd: "Moins d'arrêts de travail, moins de TMS, des équipes engagées : nous vous aidons à chiffrer le retour sur investissement de chaque action de prévention.", ed: "Fewer sick days, fewer MSDs, engaged teams: we help you quantify the ROI of every prevention initiative." },
              ].map((c, i) => (
                <Reveal key={c.ft} delay={i * 0.08}>
                  <div className="p-8 h-full" style={{ background: "var(--white)" }} data-testid={`why-card-${i}`}>
                    <c.icon size={28} style={{ color: "var(--orange)" }} />
                    <h3 className="font-display font-bold text-xl mt-5 mb-3">{t(c.ft, c.et)}</h3>
                    <p style={{ color: "var(--ink-2)" }}>{t(c.fd, c.ed)}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          {/* CHIFFRES CLÉS */}
          <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-px" style={{ background: "var(--border)", border: "1px solid var(--border)" }} data-testid="key-figures">
            {[
              { v: "+2 500", fl: "Postes de travail équipés", el: "Workstations equipped" },
              { v: "193", fl: "Formations au catalogue", el: "Training courses" },
              { v: "4,6/5", fl: "Satisfaction clients", el: "Customer satisfaction" },
              { v: "48h", fl: "Réponse à vos devis", el: "Quote response time" },
            ].map((s, i) => (
              <Reveal key={s.fl} delay={i * 0.06}>
                <div className="p-8 text-center" style={{ background: "var(--ink)", color: "var(--bone)" }} data-testid={`figure-${i}`}>
                  <p className="font-display font-black text-4xl md:text-5xl" style={{ color: "var(--orange)" }}>{s.v}</p>
                  <p className="overline mt-3" style={{ color: "rgba(245,245,240,0.7)" }}>{t(s.fl, s.el)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* VIDÉO PRÉSENTATION */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-24 md:pb-40" data-testid="video-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-12">
          <div className="lg:col-span-8">
            <Reveal><p className="overline mb-4" style={{ color: "var(--orange)" }}>{t("En images", "In motion")}</p></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none">
                {t("La prévention en action, au cœur de vos équipes.", "Prevention in action, at the heart of your teams.")}
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.1}>
            <p className="lg:col-span-4 text-lg" style={{ color: "var(--ink-2)" }}>
              {t("Audits terrain, ateliers ergonomie, formations en situation réelle : découvrez comment nous travaillons aux côtés de nos clients.",
                 "Field audits, ergonomics workshops, hands-on training: see how we work alongside our clients.")}
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.1}>
          <div className="relative overflow-hidden group cursor-pointer" style={{ border: "1px solid var(--border)" }}
            onClick={() => {
              if (!videoRef.current) return;
              if (videoRef.current.paused) { videoRef.current.play(); setPlaying(true); }
              else { videoRef.current.pause(); setPlaying(false); }
            }}
            data-testid="presentation-video-wrapper">
            <video ref={videoRef} src="/videos/presentation.mp4" poster="/videos/poster.jpg" preload="none" loop playsInline
              className="w-full h-[320px] md:h-[560px] object-cover" data-testid="presentation-video" onEnded={() => setPlaying(false)} />
            {!playing && (
              <div className="absolute inset-0 flex items-center justify-center" style={{ background: "rgba(20,20,18,0.35)" }}>
                <div className="flex items-center justify-center w-20 h-20 md:w-24 md:h-24 rounded-full transition-transform duration-300 group-hover:scale-110"
                  style={{ background: "var(--orange)" }} data-testid="video-play-btn">
                  <Play size={34} fill="var(--bone)" style={{ color: "var(--bone)", marginLeft: 4 }} />
                </div>
              </div>
            )}
            <div className="absolute bottom-0 left-0 px-6 py-4 hidden md:block" style={{ background: "var(--ink)", color: "var(--bone)" }}>
              <p className="overline">{t("ALDOL-SOLUTIONS · Santé & sécurité au travail", "ALDOL-SOLUTIONS · Workplace health & safety")}</p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* FORMATION À LA UNE */}
      {featured && (
        <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-24 md:pb-40" data-testid="featured-formation-section">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-0" style={{ border: "1px solid var(--border)" }}>
            <div className="lg:col-span-5 relative overflow-hidden min-h-[280px]">
              <img src={featured.image} alt={featured.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
            </div>
            <div className="lg:col-span-7 p-8 md:p-14" style={{ background: "var(--ink)", color: "var(--bone)" }}>
              <Reveal>
                <p className="overline mb-3" style={{ color: "var(--orange)" }}>{t("Formation à la une", "Featured course")} · {featured.domain}</p>
                <h2 className="font-display font-black text-3xl md:text-4xl tracking-tight leading-tight">{featured.title}</h2>
                {featured.rating_avg > 0 && (
                  <p className="mt-3 text-sm font-bold" style={{ color: "var(--orange)" }}>★ {String(featured.rating_avg).replace(".", ",")}/5 · {featured.rating_count} {t("avis", "reviews")}</p>
                )}
                <p className="mt-5 max-w-xl" style={{ color: "rgba(245,245,240,0.75)" }}>{(featured.description || "").slice(0, 220)}…</p>
                <div className="mt-6 flex flex-wrap items-center gap-4">
                  <VideoPresentation endpoint={`/formations/${featured.id}`} />
                  <Link to={`/formations/${featured.id}`} className="mt-5 px-6 py-3.5 font-semibold inline-flex items-center gap-2" data-testid="featured-formation-link"
                    style={{ border: "1px solid rgba(245,245,240,0.35)", color: "var(--bone)" }}>
                    {t("Voir la fiche", "View course")} <ArrowRight size={16} />
                  </Link>
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* PRODUCT SPOTLIGHT */}
      <section style={{ background: "var(--ink)", color: "var(--bone)" }} className="py-24 md:py-40">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
            <Reveal>
              <p className="overline mb-4" style={{ color: "var(--orange)" }}>{t("Sélection", "Featured")}</p>
              <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none">{t("Produits phares", "Flagship products")}</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/catalogue" data-testid="spotlight-all-btn" className="link-hover flex items-center gap-2 font-semibold" style={{ color: "var(--bone)" }}>
                {t("Voir tout le catalogue", "View full catalogue")} <ArrowUpRight size={18} />
              </Link>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px" style={{ background: "rgba(245,245,240,0.15)" }}>
            {products.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.1}>
                <Link to="/catalogue" className="group block p-6" style={{ background: "var(--ink)" }} data-testid={`spotlight-product-${i}`}>
                  <div className="overflow-hidden mb-6">
                    <img src={p.image} alt={p.name} className="w-full h-64 object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="flex justify-between items-start gap-4">
                    <h3 className="font-display font-bold text-xl">{p.name}</h3>
                    <span className="font-mono2 whitespace-nowrap" style={{ color: "var(--orange)" }}>Sur devis</span>
                  </div>
                  <p className="mt-2 text-sm" style={{ color: "rgba(245,245,240,0.6)" }}>{p.short}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* NOTRE MÉTHODE */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-40" data-testid="method-section">
        <Reveal><p className="overline mb-4" style={{ color: "var(--orange)" }}>{t("Notre méthode", "Our method")}</p></Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none max-w-3xl">
            {t("Un accompagnement clair, de A à Z.", "A clear process, from A to Z.")}
          </h2>
        </Reveal>
        <div className="mt-20 grid grid-cols-1 md:grid-cols-4 gap-px" style={{ background: "var(--border)", border: "1px solid var(--border)" }}>
          {[
            { icon: SearchCheck, n: "01", ft: "Diagnostic", et: "Diagnosis", fd: "Nous analysons vos postes de travail, vos risques et vos obligations réglementaires, sur site ou à distance.", ed: "We analyse your workstations, risks and regulatory obligations, on site or remotely." },
            { icon: ClipboardList, n: "02", ft: "Recommandation", et: "Recommendation", fd: "Vous recevez un plan d'action chiffré et priorisé : matériel adapté, formations pertinentes, mises en conformité.", ed: "You receive a costed, prioritised action plan: suitable equipment, relevant training, compliance actions." },
            { icon: Rocket, n: "03", ft: "Déploiement", et: "Deployment", fd: "Livraison, installation, sessions de formation en intra : nous orchestrons tout, sans perturber votre activité.", ed: "Delivery, installation, on-site training sessions: we orchestrate everything without disrupting your business." },
            { icon: LineChart, n: "04", ft: "Suivi & mesure", et: "Follow-up & metrics", fd: "Bilan à froid, indicateurs de prévention et ajustements : votre démarche progresse dans la durée.", ed: "Post-project review, prevention KPIs and adjustments: your programme keeps improving over time." },
          ].map((s, i) => (
            <Reveal key={s.n} delay={i * 0.08}>
              <div className="p-8 h-full flex flex-col" style={{ background: "var(--white)" }} data-testid={`method-step-${i}`}>
                <div className="flex items-center justify-between">
                  <s.icon size={26} style={{ color: "var(--orange)" }} />
                  <span className="font-mono2 text-sm" style={{ color: "var(--orange)" }}>{s.n}</span>
                </div>
                <h3 className="font-display font-bold text-2xl mt-6 mb-3">{t(s.ft, s.et)}</h3>
                <p style={{ color: "var(--ink-2)" }}>{t(s.fd, s.ed)}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ROI PRÉVENTION */}
      <section style={{ background: "var(--ink)", color: "var(--bone)" }} className="py-24 md:py-40" data-testid="roi-section">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Reveal><p className="overline mb-4" style={{ color: "var(--orange)" }}>{t("La prévention, un investissement", "Prevention pays off")}</p></Reveal>
            <Reveal delay={0.05}>
              <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none">
                {t("Chaque euro investi dans la prévention en rapporte plus de deux.", "Every euro invested in prevention returns more than two.")}
              </h2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="mt-8 text-lg max-w-2xl" style={{ color: "rgba(245,245,240,0.75)" }}>
                {t("Les troubles musculo-squelettiques représentent 87 % des maladies professionnelles en France et coûtent en moyenne 21 500 € par salarié touché. Un poste bien équipé, des équipes formées et une démarche QSE structurée réduisent l'absentéisme, sécurisent votre conformité et renforcent votre marque employeur.",
                   "Musculoskeletal disorders account for 87% of occupational diseases in France and cost an average of €21,500 per affected employee. Well-equipped workstations, trained teams and a structured HSE approach reduce absenteeism, secure compliance and strengthen your employer brand.")}
              </p>
              <div className="mt-10 flex flex-col sm:flex-row gap-4">
                <Link to="/contact" data-testid="roi-audit-btn" className="btn-primary px-8 py-4 font-semibold inline-flex items-center justify-center gap-2">
                  {t("Demander un diagnostic gratuit", "Request a free assessment")} <ArrowRight size={18} />
                </Link>
                <Link to="/formations" data-testid="roi-formations-btn" className="px-8 py-4 font-semibold inline-flex items-center justify-center gap-2" style={{ border: "1px solid rgba(245,245,240,0.35)", color: "var(--bone)" }}>
                  {t("Voir les 193 formations", "Browse 193 courses")}
                </Link>
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5 grid grid-cols-1 gap-px" style={{ background: "rgba(245,245,240,0.15)", border: "1px solid rgba(245,245,240,0.15)" }}>
            {[
              { v: "2,2 €", fl: "gagnés pour 1 € investi en prévention (AISS)", el: "returned for every €1 invested in prevention (ISSA)" },
              { v: "-25 %", fl: "d'absentéisme constaté après une démarche ergonomique structurée", el: "absenteeism observed after a structured ergonomics programme" },
              { v: "87 %", fl: "des maladies professionnelles sont des TMS évitables", el: "of occupational diseases are preventable MSDs" },
            ].map((s, i) => (
              <Reveal key={s.v} delay={i * 0.08}>
                <div className="p-8 flex items-center gap-6" style={{ background: "var(--ink)" }} data-testid={`roi-stat-${i}`}>
                  <p className="font-display font-black text-4xl md:text-5xl whitespace-nowrap" style={{ color: "var(--orange)" }}>{s.v}</p>
                  <p style={{ color: "rgba(245,245,240,0.75)" }}>{t(s.fl, s.el)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* SECTEURS CLIENTS */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32" data-testid="sectors-section">
        <Reveal><p className="overline mb-4 text-center" style={{ color: "var(--orange)" }}>{t("Ils nous font confiance", "They trust us")}</p></Reveal>
        <Reveal delay={0.05}>
          <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight text-center max-w-2xl mx-auto leading-tight">
            {t("Des ETI aux grands groupes, nous intervenons là où la sécurité est critique.", "From mid-caps to large groups, we operate where safety is critical.")}
          </h2>
        </Reveal>
        <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px" style={{ background: "var(--border)", border: "1px solid var(--border)" }}>
          {[
            { icon: Factory, fl: "Industrie", el: "Manufacturing" },
            { icon: Warehouse, fl: "Logistique", el: "Logistics" },
            { icon: HardHat, fl: "BTP", el: "Construction" },
            { icon: Stethoscope, fl: "Santé & médico-social", el: "Healthcare" },
            { icon: Building2, fl: "Tertiaire & bureaux", el: "Offices & services" },
            { icon: Landmark, fl: "Collectivités", el: "Public sector" },
          ].map((s, i) => (
            <Reveal key={s.fl} delay={i * 0.05}>
              <div className="p-8 h-full flex flex-col items-center text-center gap-4" style={{ background: "var(--white)" }} data-testid={`sector-${i}`}>
                <s.icon size={30} style={{ color: "var(--orange)" }} />
                <p className="font-display font-bold">{t(s.fl, s.el)}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ÉTUDE DE CAS */}
      <section style={{ background: "var(--white)", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }} className="py-24 md:py-40" data-testid="case-study-section">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Reveal><p className="overline mb-4" style={{ color: "var(--orange)" }}>{t("Étude de cas", "Case study")}</p></Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <Reveal delay={0.05}>
                <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-none">
                  {t("Plateforme logistique (Val-de-Marne) : -32 % de TMS en 18 mois.", "Logistics hub (Val-de-Marne): -32% MSDs in 18 months.")}
                </h2>
              </Reveal>
              <Reveal delay={0.1}>
                <div className="mt-8 space-y-5 text-lg" style={{ color: "var(--ink-2)" }}>
                  <p>
                    <strong style={{ color: "var(--ink)" }}>{t("Le contexte : ", "Context: ")}</strong>
                    {t("un site de 120 salariés confronté à une hausse des déclarations de TMS chez les préparateurs de commandes et à un absentéisme de 9,2 %.",
                       "a 120-employee site facing a rise in MSD claims among order pickers and 9.2% absenteeism.")}
                  </p>
                  <p>
                    <strong style={{ color: "var(--ink)" }}>{t("Notre intervention : ", "Our intervention: ")}</strong>
                    {t("audit ergonomique complet des postes, déploiement de 2 exosquelettes lombaires en location, équipement de 40 postes (sièges, tables à niveau constant), formation PRAP de 85 opérateurs et accompagnement du CSE sur 6 mois.",
                       "full ergonomic audit, deployment of 2 rented lumbar exoskeletons, equipping 40 workstations (seats, spring-level tables), PRAP training for 85 operators and 6 months of works-council support.")}
                  </p>
                  <p>
                    <strong style={{ color: "var(--ink)" }}>{t("Le résultat : ", "The result: ")}</strong>
                    {t("des indicateurs santé-sécurité en nette amélioration dès la première année, un investissement amorti en 14 mois grâce à la baisse de l'absentéisme, et des équipes qui recommandent la démarche.",
                       "clearly improved health & safety KPIs within the first year, investment paid back in 14 months through lower absenteeism, and teams who recommend the approach.")}
                  </p>
                </div>
                <Link to="/contact" data-testid="case-study-cta" className="btn-primary mt-10 px-8 py-4 font-semibold inline-flex items-center gap-2">
                  {t("Obtenir les mêmes résultats", "Get the same results")} <ArrowRight size={18} />
                </Link>
                <Link to="/references" data-testid="all-references-link" className="mt-10 ml-0 sm:ml-4 px-8 py-4 font-semibold inline-flex items-center gap-2 btn-outline">
                  {t("Toutes nos références", "All case studies")} <ArrowUpRight size={18} />
                </Link>
              </Reveal>
            </div>
            <div className="lg:col-span-5 grid grid-cols-1 gap-px self-start" style={{ background: "var(--border)", border: "1px solid var(--border)" }}>
              {[
                { v: "-32 %", fl: "de TMS déclarés en 18 mois", el: "declared MSDs in 18 months" },
                { v: "9,2 % → 6,8 %", fl: "d'absentéisme sur le site", el: "site absenteeism" },
                { v: "14 mois", fl: "pour amortir l'investissement", el: "to pay back the investment" },
                { v: "92 %", fl: "des opérateurs satisfaits du nouveau matériel", el: "of operators satisfied with the new equipment" },
              ].map((s, i) => (
                <Reveal key={s.v} delay={i * 0.06}>
                  <div className="p-7 flex items-center gap-6" style={{ background: "var(--bone)" }} data-testid={`case-stat-${i}`}>
                    <p className="font-display font-black text-2xl md:text-3xl whitespace-nowrap" style={{ color: "var(--orange)" }}>{s.v}</p>
                    <p style={{ color: "var(--ink-2)" }}>{t(s.fl, s.el)}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TÉMOIGNAGES */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-40" data-testid="testimonials-section">
        <Reveal><p className="overline mb-4" style={{ color: "var(--orange)" }}>Ils nous font confiance</p></Reveal>
        <Reveal delay={0.05}><h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none max-w-3xl mb-16">La parole à nos clients</h2></Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px" style={{ background: "var(--border)", border: "1px solid var(--border)" }}>
          {[
            { q: "Un accompagnement QSE d'une grande rigueur. Notre certification ISO 45001 a été obtenue du premier coup.", n: "Aïcha Diallo", r: "Responsable QSE, Groupe Logistique", img: "https://static.prod-images.emergentagent.com/jobs/3605812b-40ae-4438-9f86-28dfb90c74fb/images/5c4b0c43883c3e7ce76237384bd2bd32b7ace6fd6ed485f08dc9513c6fab8c99.jpeg" },
            { q: "Les formations sécurité ont transformé notre culture de prévention. Des formateurs à l'écoute et très concrets.", n: "Rahul Sharma", r: "Directeur HSE, Industrie", img: "https://static.prod-images.emergentagent.com/jobs/3605812b-40ae-4438-9f86-28dfb90c74fb/images/c053abd3e17b59cba6e8d9c2312f8981ca5d237fbeb023872d4acd80d579b9b2.jpeg" },
            { q: "Leur démarche QVT a nettement amélioré l'engagement de nos équipes. Un vrai partenaire de confiance.", n: "Mei Chen", r: "Directrice des Ressources Humaines", img: "https://static.prod-images.emergentagent.com/jobs/3605812b-40ae-4438-9f86-28dfb90c74fb/images/3ab696549a9513e9e4434d885d2734e4746a5cb0391a4cc85de12105d8bf1ee4.jpeg" },
          ].map((t, i) => (
            <Reveal key={t.n} delay={i * 0.1}>
              <div className="p-8 h-full flex flex-col" style={{ background: "var(--white)" }} data-testid={`testimonial-${i}`}>
                <p className="text-lg flex-1" style={{ color: "var(--ink)" }}>“{t.q}”</p>
                <div className="mt-8 flex items-center gap-4">
                  <img src={t.img} alt={t.n} className="w-14 h-14 object-cover rounded-full" style={{ border: "2px solid var(--orange)" }} />
                  <div>
                    <p className="font-display font-bold">{t.n}</p>
                    <p className="text-sm" style={{ color: "var(--ink-2)" }}>{t.r}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* AVIS PRODUITS */}
      {reviews.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-24 md:pb-40" data-testid="product-reviews-section">
          <Reveal><p className="overline mb-4" style={{ color: "var(--orange)" }}>{t("Avis vérifiés", "Verified reviews")}</p></Reveal>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <Reveal delay={0.05}>
              <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none max-w-3xl">{t("Nos produits notés par nos clients", "Rated by our customers")}</h2>
            </Reveal>
            <Reveal delay={0.1}>
              <Link to="/catalogue" data-testid="reviews-catalogue-link" className="btn-outline px-6 py-3 font-semibold inline-flex items-center gap-2 whitespace-nowrap">
                {t("Voir le catalogue", "View catalogue")} <ArrowRight size={16} />
              </Link>
            </Reveal>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px" style={{ background: "var(--border)", border: "1px solid var(--border)" }}>
            {reviews.slice(0, 6).map((r, i) => {
              const isF = r.kind === "formation";
              const img = isF ? r.formation_image : r.product_image;
              const name = isF ? r.formation_title : r.product_name;
              const link = isF ? `/formations/${r.formation_id}` : `/catalogue/${r.product_id}`;
              return (
              <Reveal key={r.id} delay={i * 0.06}>
                <div className="p-7 h-full flex flex-col" style={{ background: "var(--white)" }} data-testid={`product-review-${i}`}>
                  <Stars value={r.rating} size={15} />
                  {r.verified && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 mt-2 self-start" style={{ background: "rgba(62,142,90,0.12)", color: "#3E8E5A" }} data-testid={`home-verified-${i}`}>
                      Achat vérifié
                    </span>
                  )}
                  {isF && (
                    <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 mt-2 self-start" style={{ background: "rgba(232,93,4,0.1)", color: "var(--orange)" }} data-testid={`home-formation-tag-${i}`}>
                      Formation
                    </span>
                  )}
                  <p className="mt-4 flex-1" style={{ color: "var(--ink)" }}>“{r.comment}”</p>
                  <div className="mt-6 flex items-center gap-4">
                    {img && (
                      <Link to={link} className="shrink-0">
                        <img src={img} alt={name} loading="lazy" className="w-12 h-12 object-cover" style={{ border: "1px solid var(--border)" }} />
                      </Link>
                    )}
                    <div className="min-w-0">
                      <p className="font-display font-bold truncate">{r.name}</p>
                      <Link to={link} className="text-sm truncate block hover:underline" style={{ color: "var(--ink-2)" }}>
                        {name}
                      </Link>
                    </div>
                  </div>
                </div>
              </Reveal>
              );
            })}
          </div>
        </section>
      )}

      {/* GARANTIES */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-24 md:pb-40" data-testid="guarantees-section">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px" style={{ background: "var(--border)", border: "1px solid var(--border)" }}>
          {[
            { icon: Truck, ft: "Livraison 48h", et: "48h delivery", fd: "Sur les produits en stock, partout en France métropolitaine.", ed: "On in-stock products, across mainland France." },
            { icon: BadgeCheck, ft: "Matériel certifié CE", et: "CE-certified equipment", fd: "Chaque référence est contrôlée et conforme aux normes en vigueur.", ed: "Every product is checked and compliant with current standards." },
            { icon: ShieldCheck, ft: "Paiement sécurisé", et: "Secure payment", fd: "Transactions protégées par Stripe. Devis et mandat administratif acceptés.", ed: "Transactions secured by Stripe. Quotes and purchase orders accepted." },
            { icon: Award, ft: "Devis gratuit sous 48h", et: "Free quote within 48h", fd: "Une réponse personnalisée et sans engagement pour chaque demande.", ed: "A personalised, no-obligation answer for every request." },
          ].map((g, i) => (
            <Reveal key={g.ft} delay={i * 0.06}>
              <div className="p-7 h-full" style={{ background: "var(--white)" }} data-testid={`guarantee-${i}`}>
                <g.icon size={24} style={{ color: "var(--orange)" }} />
                <h3 className="font-display font-bold text-lg mt-4 mb-2">{t(g.ft, g.et)}</h3>
                <p className="text-sm" style={{ color: "var(--ink-2)" }}>{t(g.fd, g.ed)}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* LIVRE BLANC */}
      <LeadMagnet />

      {/* PRÉSENCE INTERNATIONALE */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 pb-24 md:pb-32" data-testid="international-band">
        <Reveal>
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 p-8 md:p-12" style={{ background: "var(--white)", border: "1px solid var(--border)" }}>
            <div>
              <p className="overline mb-3" style={{ color: "var(--orange)" }}>{t("Présence internationale", "International presence")}</p>
              <h2 className="font-display font-black text-2xl md:text-3xl tracking-tight max-w-2xl leading-tight">
                {t("Présents en France, au Cameroun, au Sénégal, au Gabon et en Côte d'Ivoire.", "Operating in France, Cameroon, Senegal, Gabon and Ivory Coast.")}
              </h2>
            </div>
            <Link to="/international" className="btn-primary px-7 py-3.5 font-semibold inline-flex items-center gap-2 whitespace-nowrap" data-testid="international-band-btn">
              {t("Découvrir nos implantations", "Discover our locations")} <ArrowRight size={17} />
            </Link>
          </div>
        </Reveal>
      </section>

      {/* ÉQUIPE */}
      <TeamBand />

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-40">
        <Reveal>
          <div className="p-10 md:p-20 text-center md:text-left flex flex-col md:flex-row md:items-center justify-between gap-10" style={{ background: "var(--orange)", color: "var(--bone)" }}>
            <div>
              <h2 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none">{t("Faisons de votre sécurité un avantage.", "Let's turn your safety into an edge.")}</h2>
              <p className="mt-5 text-lg md:text-xl max-w-lg" style={{ color: "rgba(245,245,240,0.9)" }}>
                {t("Exposez-nous votre enjeu. Un expert vous répond sous 48h avec une recommandation chiffrée.", "Tell us your challenge. An expert answers within 48h with a costed recommendation.")}
              </p>
            </div>
            <Link to="/contact" data-testid="cta-contact-btn" className="px-10 py-5 font-display font-bold text-lg whitespace-nowrap" style={{ background: "var(--ink)", color: "var(--bone)" }}>
              {t("Nous contacter", "Contact us")}
            </Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
